// Layout calculation types and utilities. Primarily concerned with calculating
// layout statistics

export interface FrameLayoutInfo {
  childCount: number;

  // Frame dimensions
  frameWidth: number | null;
  frameHeight: number | null;
  effectiveWidth: number;
  effectiveHeight: number;
  maxSize: { width: number | null; height: number | null };

  // Spacing
  padding: SpacingSidesStruct;
  gap: number;

  // Child measurements
  maxChildWidth: number;
  maxChildHeight: number;

  // TODO better name
  total: { width: number; height: number };
}

// Compute statistics for the layout.
export function calculateFrameLayout(frame: any): FrameLayoutInfo {
  const children = frame.sortedChildren();
  const padding = frame.layoutPaddingSides();
  const isHorizontal = frame.layoutDirection === 'horizontal';

  // Get the size which controls how other settings will apply.
  const [elementWidth, elementHeight] = frame.layoutSize();
  let [maxWidth, maxHeight] = [elementWidth, elementHeight];

  if (maxWidth) maxWidth -= padding.left + padding.right;
  if (maxHeight) maxHeight -= padding.top + padding.bottom;

  const maxSize = { width: maxWidth, height: maxHeight };

  // Track total used space of children and gap and maximums
  const total = { width: 0, height: 0 };
  const maxChild = { width: 0, height: 0 };

  children.forEach(child => {
    let childWidth = child.width * child.scaleX;
    let childHeight = child.height * child.scaleY;

    if (child.strokeWidth) {
      childWidth += child.strokeWidth;
      childHeight += child.strokeWidth;
    }

    total.width += childWidth;
    total.height += childHeight;
    maxChild.width = Math.max(maxChild.width, childWidth);
    maxChild.height = Math.max(maxChild.height, childHeight);
  });

  if (isHorizontal) {
    if (!maxSize.height) {
      maxSize.height = maxChild.height;
    }
  } else {
    if (!maxSize.width) {
      maxSize.width = maxChild.width;
    }
  }

  // Calculate gap
  let gap = 0;
  const mainAxisSize = isHorizontal ? maxWidth : maxHeight;
  const mainAxisTotal = isHorizontal ? total.width : total.height;

  if (frame.layoutGap === 'auto' && mainAxisSize && children.length > 1) {
    gap = (mainAxisSize - mainAxisTotal) / (children.length - 1);
  } else if (frame.layoutGap !== 'auto') {
    gap = frame.layoutGap;
  }

  if (isHorizontal) {
    total.width += gap * (children.length - 1);
  } else {
    total.height += gap * (children.length - 1);
  }

  return {
    childCount: children.length,

    frameWidth: elementWidth,
    frameHeight: elementHeight,
    effectiveWidth: maxWidth ?? 0,
    effectiveHeight: maxHeight ?? 0,
    maxSize,

    padding,
    gap,

    total,
    maxChildWidth: maxChild.width,
    maxChildHeight: maxChild.height,
  };
}
